import React, { useState, useEffect, useCallback } from 'react';
import { FileText, BarChart3, History, Loader2, CheckCircle, AlertCircle, Download, Eye, Trash2, Pin, Search, Calendar, X, RefreshCw, FileSpreadsheet, Clock, Share2, Users, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiClient, ReportType, ReportParameters, Report, ReportDetail, ReportSchedule } from '../services/api';
import ScheduleModal from './Modals/ScheduleModal';
import ShareReportModal from './Modals/ShareReportModal';
import { useLocation, useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface ReportsProps {
    user: any;
}

const Reports: React.FC<ReportsProps> = ({ user }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'generate' | 'history' | 'schedules' | 'shared'>('generate');
    const [initialReportId, setInitialReportId] = useState<string | null>(null);

    // Check for reportId and tab in URL on mount
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const reportId = searchParams.get('reportId');
        const tab = searchParams.get('tab');

        if (reportId) {
            setActiveTab('history');
            setInitialReportId(reportId);
            // Clear the URL parameter after capturing it
            navigate('/reports', { replace: true });
        } else if (tab === 'history' || tab === 'schedules' || tab === 'generate' || tab === 'shared') {
            setActiveTab(tab as 'generate' | 'history' | 'schedules' | 'shared');
            // Clear the URL parameter after capturing it
            navigate('/reports', { replace: true });
        }
    }, [location.search, navigate]);

    // Helper function to switch tabs and clear initialReportId
    const handleTabChange = (tab: 'generate' | 'history' | 'schedules' | 'shared') => {
        setActiveTab(tab);
        setInitialReportId(null);
    };

    return (
        <div className="max-w-7xl mx-auto px-4 py-8">
            {/* Print Header */}
            <div className="hidden print:block border-b-2 pb-4 mb-8" style={{ borderColor: '#231f20' }}>
                <div className="flex items-center justify-between">
                    <img
                        src="/logo-horizontal-black.png"
                        alt="IntelliDrive"
                        className="h-20 w-auto"
                    />
                    <div className="text-right">
                        <h1 className="text-lg font-bold text-gray-900">
                            IntelliDrive Reports
                        </h1>
                        <p className="text-sm text-gray-600">
                            Generated: {new Date().toLocaleDateString('en-GB')}
                        </p>
                    </div>
                </div>
            </div>

            {/* Header */}
            <div className="mb-6">
                <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Reports</h1>
                    </div>
                </div>
                <p className="text-sm sm:text-base text-gray-600">
                    Generate compliance and analytics reports for your organisation
                </p>
            </div>

            {/* Tab Navigation */}
            <div className="mb-6 bg-white rounded-lg shadow">
                <div className="border-b border-gray-200">
                    <nav className="-mb-px flex">
                        <button
                            onClick={() => handleTabChange('generate')}
                            className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === 'generate'
                                    ? 'border-green-500 text-gray-900'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                            style={activeTab === 'generate' ? { borderColor: '#69BE28', color: '#231f20' } : {}}
                        >
                            <FileText className="w-5 h-5 mr-2" />
                            Generate Report
                        </button>
                        <button
                            onClick={() => handleTabChange('history')}
                            className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === 'history'
                                    ? 'border-green-500 text-gray-900'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                            style={activeTab === 'history' ? { borderColor: '#69BE28', color: '#231f20' } : {}}
                        >
                            <History className="w-5 h-5 mr-2" />
                            Report History
                        </button>
                        <button
                            onClick={() => handleTabChange('schedules')}
                            className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === 'schedules'
                                    ? 'border-green-500 text-gray-900'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                            style={activeTab === 'schedules' ? { borderColor: '#69BE28', color: '#231f20' } : {}}
                        >
                            <Clock className="w-5 h-5 mr-2" />
                            Scheduled Reports
                        </button>
                        <button
                            onClick={() => handleTabChange('shared')}
                            className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === 'shared'
                                    ? 'border-green-500 text-gray-900'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                            style={activeTab === 'shared' ? { borderColor: '#69BE28', color: '#231f20' } : {}}
                        >
                            <Users className="w-5 h-5 mr-2" />
                            Shared with Me
                        </button>
                    </nav>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                    {activeTab === 'generate' ? (
                        <GenerateReportTab user={user} />
                    ) : activeTab === 'history' ? (
                        <ReportHistoryTab user={user} initialReportId={initialReportId} />
                    ) : activeTab === 'schedules' ? (
                        <ScheduledReportsTab user={user} />
                    ) : (
                        <SharedWithMeTab user={user} />
                    )}
                </div>
            </div>
        </div>
    );
};

// Generate Report Tab Component
const GenerateReportTab: React.FC<{ user: any }> = ({ user }) => {
    const [reportType, setReportType] = useState<ReportType>('compliance');
    const [reportName, setReportName] = useState('');
    const [dateRange, setDateRange] = useState<'last_7' | 'last_30' | 'last_90' | 'custom' | 'all'>('last_30');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');

    // Compliance filters
    const [consentStatus, setConsentStatus] = useState<'all' | 'active' | 'expired' | 'expiring_soon' | 'no_consent'>('all');

    // Risk Analysis filters
    const [riskLevel, setRiskLevel] = useState<'all' | 'low' | 'medium' | 'high'>('all');

    // Licence Check filters
    const [checkStatus, setCheckStatus] = useState<'all' | 'valid' | 'invalid' | 'expired' | 'unknown'>('all');

    // Email Delivery filters
    const [deliveryStatus, setDeliveryStatus] = useState<'all' | 'delivered' | 'bounced' | 'dropped' | 'spam' | 'sent' | 'deferred'>('all');

    // Common filters
    const [driverStatus, setDriverStatus] = useState<'all' | 'active' | 'inactive'>('active');

    // Generation state
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatedReportId, setGeneratedReportId] = useState<string | null>(null);
    const [showGeneratedReportModal, setShowGeneratedReportModal] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);

    // Report type configurations
    const reportTypes = [
        { value: 'compliance', label: 'Compliance Report', description: 'Driver consent status and expiry tracking' },
        { value: 'risk_analysis', label: 'Risk Analysis Report', description: 'Risk distribution and high-risk drivers' },
        { value: 'licence_check', label: 'Licence Check Report', description: 'Licence check history and success rates' },
        { value: 'email_delivery', label: 'Email Delivery Report', description: 'Email delivery status and bounce tracking' },
        { value: 'audit_activity', label: 'Audit Activity Report', description: 'User activity and system changes' }
    ];

    const handleGenerateReport = async () => {
        // Validation
        if (!reportName.trim()) {
            toast.error('Please enter a report name');
            return;
        }

        if (dateRange === 'custom' && (!customStartDate || !customEndDate)) {
            toast.error('Please select both start and end dates');
            return;
        }

        setIsGenerating(true);
        setGeneratedReportId(null);

        try {
            // Build parameters based on selected filters
            const parameters: ReportParameters = {
                driverStatus: driverStatus === 'all' ? undefined : driverStatus
            };

            // Add date range if not 'all'
            if (dateRange !== 'all') {
                const now = new Date();
                let startDate: Date;
                const endDate = new Date();

                switch (dateRange) {
                    case 'last_7':
                        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                        break;
                    case 'last_30':
                        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                        break;
                    case 'last_90':
                        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
                        break;
                    case 'custom':
                        startDate = new Date(customStartDate);
                        endDate.setTime(new Date(customEndDate).getTime());
                        break;
                    default:
                        startDate = new Date(0);
                }

                parameters.dateRange = {
                    start: startDate.toISOString(),
                    end: endDate.toISOString()
                };
            }

            // Add report-specific filters
            switch (reportType) {
                case 'compliance':
                    if (consentStatus !== 'all') {
                        parameters.consentStatus = consentStatus;
                    }
                    break;
                case 'risk_analysis':
                    if (riskLevel !== 'all') {
                        parameters.riskLevel = riskLevel;
                    }
                    break;
                case 'licence_check':
                    if (checkStatus !== 'all') {
                        parameters.checkStatus = checkStatus;
                    }
                    break;
                case 'email_delivery':
                    if (deliveryStatus !== 'all') {
                        parameters.deliveryStatus = deliveryStatus;
                    }
                    break;
            }

            // Generate report
            const response = await apiClient.generateReport(reportType, reportName, parameters);

            setGeneratedReportId(response.data.id);
            setShowSuccessMessage(true);
            toast.success(`Report generated successfully! ${response.data.rowCount} rows.`);

            // Reset form
            setReportName('');
            setDateRange('last_30');
            setConsentStatus('all');
            setRiskLevel('all');
            setCheckStatus('all');
            setDeliveryStatus('all');
            setDriverStatus('active');

        } catch (error: any) {
            console.error('Error generating report:', error);
            toast.error(error.message || 'Failed to generate report');
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Report Visibility Info Box */}
            <div className="flex items-start space-x-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Who can see your reports?</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                        <li><strong>You</strong> can always see reports you create</li>
                        <li><strong>Admins, Managers & Compliance Officers</strong> can see all reports in your organisation</li>
                        <li><strong>Other users</strong> can only see reports if you explicitly share them (using the Share button in Report History)</li>
                        <li><strong>Organisation-wide reports</strong> (if enabled by admins) are visible to everyone in your organisation</li>
                    </ul>
                </div>
            </div>

            {/* Report Type Selection */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                    Select Report Type
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {reportTypes.map((type) => (
                        <button
                            key={type.value}
                            onClick={() => setReportType(type.value as ReportType)}
                            className={`p-4 rounded-lg border-2 text-left transition-all ${
                                reportType === type.value
                                    ? 'border-green-500 bg-green-50'
                                    : 'border-gray-200 hover:border-gray-300 bg-white'
                            }`}
                            style={reportType === type.value ? { borderColor: '#69BE28', backgroundColor: '#f0fdf4' } : {}}
                        >
                            <div className="flex items-start">
                                <BarChart3
                                    className={`w-5 h-5 mr-3 mt-0.5 ${
                                        reportType === type.value ? 'text-green-600' : 'text-gray-400'
                                    }`}
                                    style={reportType === type.value ? { color: '#69BE28' } : {}}
                                />
                                <div>
                                    <h4 className="font-medium text-gray-900">{type.label}</h4>
                                    <p className="text-sm text-gray-600 mt-1">{type.description}</p>
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Report Name */}
            <div>
                <label htmlFor="reportName" className="block text-sm font-medium text-gray-700 mb-2">
                    Report Name
                </label>
                <input
                    type="text"
                    id="reportName"
                    value={reportName}
                    onChange={(e) => setReportName(e.target.value)}
                    placeholder="e.g., Monthly Compliance Report - January 2026"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
            </div>

            {/* Filters Section */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <h3 className="text-sm font-medium text-gray-900">Filters</h3>

                {/* Date Range */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date Range
                    </label>
                    <select
                        value={dateRange}
                        onChange={(e) => setDateRange(e.target.value as any)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                        <option value="last_7">Last 7 Days</option>
                        <option value="last_30">Last 30 Days</option>
                        <option value="last_90">Last 90 Days</option>
                        <option value="custom">Custom Range</option>
                        <option value="all">All Time</option>
                    </select>
                </div>

                {/* Custom Date Range */}
                {dateRange === 'custom' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Start Date
                            </label>
                            <input
                                type="date"
                                value={customStartDate}
                                onChange={(e) => setCustomStartDate(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                End Date
                            </label>
                            <input
                                type="date"
                                value={customEndDate}
                                onChange={(e) => setCustomEndDate(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            />
                        </div>
                    </div>
                )}

                {/* Report-specific filters */}
                {reportType === 'compliance' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Consent Status
                        </label>
                        <select
                            value={consentStatus}
                            onChange={(e) => setConsentStatus(e.target.value as any)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        >
                            <option value="all">All Statuses</option>
                            <option value="active">Active Consent</option>
                            <option value="expired">Expired Consent</option>
                            <option value="expiring_soon">Expiring Soon (30 days)</option>
                            <option value="no_consent">No Consent</option>
                        </select>
                    </div>
                )}

                {reportType === 'risk_analysis' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Risk Level
                        </label>
                        <select
                            value={riskLevel}
                            onChange={(e) => setRiskLevel(e.target.value as any)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        >
                            <option value="all">All Risk Levels</option>
                            <option value="low">Low Risk</option>
                            <option value="medium">Medium Risk</option>
                            <option value="high">High Risk</option>
                        </select>
                    </div>
                )}

                {reportType === 'licence_check' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Licence Status
                        </label>
                        <select
                            value={checkStatus}
                            onChange={(e) => setCheckStatus(e.target.value as any)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        >
                            <option value="all">All Statuses</option>
                            <option value="valid">Valid</option>
                            <option value="invalid">Invalid</option>
                            <option value="expired">Expired</option>
                            <option value="unknown">Unknown</option>
                        </select>
                    </div>
                )}

                {reportType === 'email_delivery' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Delivery Status
                        </label>
                        <select
                            value={deliveryStatus}
                            onChange={(e) => setDeliveryStatus(e.target.value as any)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        >
                            <option value="all">All Statuses</option>
                            <option value="delivered">Delivered</option>
                            <option value="sent">Sent</option>
                            <option value="bounced">Bounced</option>
                            <option value="dropped">Dropped</option>
                            <option value="spam">Spam</option>
                            <option value="deferred">Deferred</option>
                        </select>
                    </div>
                )}

                {/* Driver Status (common for most reports) */}
                {reportType !== 'audit_activity' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Driver Status
                        </label>
                        <select
                            value={driverStatus}
                            onChange={(e) => setDriverStatus(e.target.value as any)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        >
                            <option value="all">All Drivers</option>
                            <option value="active">Active Only</option>
                            <option value="inactive">Inactive Only</option>
                        </select>
                    </div>
                )}
            </div>

            {/* Success Message */}
            {showSuccessMessage && generatedReportId && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start" style={{ backgroundColor: '#f0fdf4', borderColor: '#86efac' }}>
                    <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" style={{ color: '#69BE28' }} />
                    <div className="flex-1">
                        <h4 className="font-medium text-gray-900">Report Generated Successfully!</h4>
                        <p className="text-sm text-gray-600 mt-1 mb-3">
                            Your report has been generated and saved to Report History.
                        </p>
                        <button
                            onClick={() => {
                                setShowGeneratedReportModal(true);
                                setShowSuccessMessage(false);
                            }}
                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                            style={{ backgroundColor: '#69BE28' }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#5ba023')}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#69BE28')}
                        >
                            <Eye className="w-4 h-4 mr-2" />
                            View Report
                        </button>
                    </div>
                </div>
            )}

            {/* Generate Button */}
            <div className="flex justify-end">
                <button
                    onClick={handleGenerateReport}
                    disabled={isGenerating}
                    className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center transition-colors"
                    style={{ backgroundColor: isGenerating ? '#d1d5db' : '#69BE28' }}
                    onMouseEnter={(e) => !isGenerating && (e.currentTarget.style.backgroundColor = '#5ba023')}
                    onMouseLeave={(e) => !isGenerating && (e.currentTarget.style.backgroundColor = '#69BE28')}
                >
                    {isGenerating ? (
                        <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Generating Report...
                        </>
                    ) : (
                        <>
                            <FileText className="w-5 h-5 mr-2" />
                            Generate Report
                        </>
                    )}
                </button>
            </div>

            {/* Report View Modal */}
            {showGeneratedReportModal && generatedReportId && (
                <ReportViewModal
                    reportId={generatedReportId}
                    onClose={() => setShowGeneratedReportModal(false)}
                    onRefresh={() => {
                        // No need to refresh anything in the Generate tab
                        // Report History tab will refresh when user switches to it
                    }}
                />
            )}
        </div>
    );
};

// Report View Modal Component
interface ReportViewModalProps {
    reportId: string;
    onClose: () => void;
    onRefresh: () => void;
}

const ReportViewModal: React.FC<ReportViewModalProps> = ({ reportId, onClose, onRefresh }) => {
    const [report, setReport] = useState<ReportDetail | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isRegenerating, setIsRegenerating] = useState(false);

    // Load report details
    useEffect(() => {
        const loadReport = async () => {
            try {
                setIsLoading(true);
                setError(null);
                const response = await apiClient.getReport(reportId);
                setReport(response.data);
            } catch (err: any) {
                console.error('Error loading report:', err);
                setError(err.message || 'Failed to load report');
            } finally {
                setIsLoading(false);
            }
        };

        loadReport();
    }, [reportId]);

    // Handle pin/unpin
    const handlePinReport = async () => {
        if (!report) return;

        try {
            await apiClient.pinReport(report.id, !report.isPinned);
            toast.success(report.isPinned ? 'Report unpinned' : 'Report pinned');

            // Update local state
            setReport({ ...report, isPinned: !report.isPinned });
            onRefresh();
        } catch (err: any) {
            toast.error(err.message || 'Failed to update report');
        }
    };

    // Handle regenerate
    const handleRegenerate = async () => {
        if (!report) return;

        setIsRegenerating(true);
        try {
            await apiClient.generateReport(report.type, `${report.reportName} (Regenerated)`, report.parameters);
            toast.success('Report regenerated successfully! Check Report History.');
            onRefresh();
            onClose();
        } catch (err: any) {
            toast.error(err.message || 'Failed to regenerate report');
        } finally {
            setIsRegenerating(false);
        }
    };

    // Handle export CSV
    const handleExportCSV = async () => {
        if (!report) return;

        try {
            await apiClient.exportReportCSV(report.id, report.reportName);
            toast.success('CSV downloaded successfully');
        } catch (err: any) {
            toast.error(err.message || 'Failed to export CSV');
        }
    };

    // Handle export PDF
    const handleExportPDF = async () => {
        if (!report) return;

        try {
            await apiClient.exportReportPDF(report.id, report.reportName);
            toast.success('PDF downloaded successfully');
        } catch (err: any) {
            toast.error(err.message || 'Failed to export PDF');
        }
    };

    // Get formatted report type label
    const getReportTypeLabel = (type: ReportType) => {
        switch (type) {
            case 'compliance':
                return 'Compliance Report';
            case 'risk_analysis':
                return 'Risk Analysis Report';
            case 'licence_check':
                return 'Licence Check Report';
            case 'email_delivery':
                return 'Email Delivery Report';
            case 'audit_activity':
                return 'Audit Activity Report';
            default:
                return type;
        }
    };

    // Format parameter display
    const formatParameterValue = (key: string, value: any): string => {
        if (value === null || value === undefined) return 'N/A';

        if (key === 'dateRange' && typeof value === 'object') {
            const start = new Date(value.start).toLocaleDateString('en-GB');
            const end = new Date(value.end).toLocaleDateString('en-GB');
            return `${start} - ${end}`;
        }

        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (typeof value === 'string') {
            // Format enum values (e.g., 'expiring_soon' -> 'Expiring Soon')
            return value.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
        }

        return String(value);
    };

    // Format parameter key for display
    const formatParameterKey = (key: string): string => {
        return key
            .split(/(?=[A-Z])/)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };
    // Render charts based on report type
    const renderChart = () => {
        if (!report || !report.dataSnapshot || !report.dataSnapshot.rows) return null;

        const rows = report.dataSnapshot.rows;
        if (rows.length === 0) return null;

        switch (report.type) {
            case 'compliance':
                return renderComplianceChart(rows);
            case 'risk_analysis':
                return renderRiskAnalysisChart(rows);
            case 'licence_check':
                return renderLicenceCheckChart(rows);
            case 'email_delivery':
                return renderEmailDeliveryChart(rows);
            case 'audit_activity':
                return renderAuditActivityChart(rows);
            default:
                return null;
        }
    };

    // Compliance Report: Pie chart of consent status
    const renderComplianceChart = (rows: any[]) => {
        const consentCounts: Record<string, number> = {};
        rows.forEach(row => {
            const status = row['Consent Status'] || row['consentStatus'] || 'Unknown';
            consentCounts[status] = (consentCounts[status] || 0) + 1;
        });

        const data = Object.entries(consentCounts).map(([name, value]) => ({ name, value }));

        return (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Consent Status Distribution</h4>
                <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                        <Pie
                            data={data}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                        >
                            {data.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={entry.name === 'No Consent' ? '#DC2626' : '#69BE28'}
                                />
                            ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        );
    };

    // Risk Analysis Report: Bar chart of risk distribution
    const renderRiskAnalysisChart = (rows: any[]) => {
        const riskCounts: Record<string, number> = { Low: 0, Medium: 0, High: 0 };
        rows.forEach(row => {
            const level = row['Risk Level'] || row['riskLevel'] || 'Unknown';
            if (riskCounts.hasOwnProperty(level)) {
                riskCounts[level]++;
            }
        });

        const data = Object.entries(riskCounts).map(([name, count]) => ({ name, count }));

        const getRiskColor = (riskLevel: string) => {
            switch (riskLevel) {
                case 'Low':
                    return '#69BE28'; // Green
                case 'Medium':
                    return '#F59E0B'; // Yellow/Amber
                case 'High':
                    return '#DC2626'; // Red
                default:
                    return '#9CA3AF'; // Gray for unknown
            }
        };

        return (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Risk Level Distribution</h4>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="count" name="Driver Count">
                            {data.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={getRiskColor(entry.name)} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        );
    };

    // Licence Check Report: Line chart of checks over time
    const renderLicenceCheckChart = (rows: any[]) => {
        // Group by date
        const dateCounts: Record<string, number> = {};
        rows.forEach(row => {
            const dateStr = row['Check Date'] || row['checkDate'];
            if (dateStr) {
                const date = new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
                dateCounts[date] = (dateCounts[date] || 0) + 1;
            }
        });

        const data = Object.entries(dateCounts)
            .map(([date, count]) => ({ date, count }))
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
            .slice(-14); // Last 14 days

        return (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Licence Checks Over Time</h4>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="count" stroke="#69BE28" strokeWidth={2} name="Checks" />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        );
    };

    // Email Delivery Report: Pie chart of delivery status
    const renderEmailDeliveryChart = (rows: any[]) => {
        const statusCounts: Record<string, number> = {};
        rows.forEach(row => {
            const status = row['deliveryStatus'] || row['Delivery Status'] || row['Status'] || row['status'] || 'Unknown';
            statusCounts[status] = (statusCounts[status] || 0) + 1;
        });

        const data = Object.entries(statusCounts).map(([name, value]) => ({ name, value }));

        const getEmailStatusColor = (status: string) => {
            switch (status.toLowerCase()) {
                case 'delivered':
                case 'sent':
                    return '#69BE28'; // Green
                case 'failed':
                case 'bounced':
                    return '#DC2626'; // Red
                case 'pending':
                case 'queued':
                    return '#F59E0B'; // Yellow/Amber
                case 'unknown':
                default:
                    return '#9CA3AF'; // Gray
            }
        };

        return (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Email Delivery Status</h4>
                <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                        <Pie
                            data={data}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                        >
                            {data.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={getEmailStatusColor(entry.name)} />
                            ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        );
    };

    // Audit Activity Report: Bar chart of activity types
    const renderAuditActivityChart = (rows: any[]) => {
        const actionCounts: Record<string, number> = {};
        rows.forEach(row => {
            const action = row['Action'] || row['action'] || 'Unknown';
            actionCounts[action] = (actionCounts[action] || 0) + 1;
        });

        const data = Object.entries(actionCounts)
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 10); // Top 10 actions

        return (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Top Activity Types</h4>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={data} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="name" type="category" width={150} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="count" fill="#69BE28" name="Count" />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-white rounded-lg max-w-6xl w-full my-8 shadow-xl">
                {/* Modal Header */}
                <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                    <div className="flex-1">
                        <h2 className="text-xl font-bold text-gray-900">Report Details</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Modal Content */}
                <div className="px-6 py-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <Loader2 className="w-12 h-12 text-gray-400 animate-spin mb-4" />
                            <p className="text-gray-600">Loading report...</p>
                        </div>
                    ) : error ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
                            <p className="text-gray-900 font-medium mb-2">Error Loading Report</p>
                            <p className="text-gray-600">{error}</p>
                        </div>
                    ) : report ? (
                        <div className="space-y-6">
                            {/* Report Metadata */}
                            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Report Name</p>
                                        <div className="flex items-center mt-1">
                                            {report.isPinned && (
                                                <Pin className="w-4 h-4 text-yellow-500 mr-2" fill="currentColor" />
                                            )}
                                            <p className="text-base text-gray-900">{report.reportName}</p>
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Report Type</p>
                                        <p className="text-base text-gray-900 mt-1">{getReportTypeLabel(report.type)}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Generated Date</p>
                                        <p className="text-base text-gray-900 mt-1">
                                            {new Date(report.generatedAt).toLocaleDateString('en-GB')}{' '}
                                            {new Date(report.generatedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Generated By</p>
                                        <p className="text-base text-gray-900 mt-1">{report.generatedBy.name}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Total Rows</p>
                                        <p className="text-base text-gray-900 mt-1">{report.rowCount}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Status</p>
                                        <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full mt-1 ${
                                            report.status === 'completed' ? 'bg-green-100 text-green-800' :
                                            report.status === 'failed' ? 'bg-red-100 text-red-800' :
                                            'bg-yellow-100 text-yellow-800'
                                        }`}>
                                            {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                                        </span>
                                    </div>
                                </div>

                                {/* Report Parameters */}
                                {Object.keys(report.parameters).length > 0 && (
                                    <div className="pt-3 border-t border-gray-200">
                                        <p className="text-sm font-medium text-gray-600 mb-2">Report Parameters</p>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {Object.entries(report.parameters).map(([key, value]) => (
                                                <div key={key} className="text-sm">
                                                    <span className="text-gray-600">{formatParameterKey(key)}:</span>{' '}
                                                    <span className="text-gray-900 font-medium">{formatParameterValue(key, value)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Summary Statistics */}
                            {report.dataSnapshot.summary && Object.keys(report.dataSnapshot.summary).length > 0 && (
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Summary</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        {Object.entries(report.dataSnapshot.summary).map(([key, value]) => {
                                            const isActionsBreakdown = key === 'actionsBreakdown';
                                            const isMostCommonAction = key === 'mostCommonAction';
                                            const isNumeric = typeof value === 'number';

                                            return (
                                                <div
                                                    key={key}
                                                    className="bg-white border border-gray-200 rounded-lg p-4"
                                                >
                                                    <p className="text-sm font-medium text-gray-600 mb-2">{formatParameterKey(key)}</p>

                                                    {isActionsBreakdown ? (
                                                        // Display Actions Breakdown as a list
                                                        <ul className="space-y-1">
                                                            {String(value).split(', ').map((action, idx) => (
                                                                <li key={idx} className="text-sm text-gray-900 flex items-center">
                                                                    <span className="w-1.5 h-1.5 rounded-full mr-2" style={{ backgroundColor: '#69BE28' }}></span>
                                                                    {action}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    ) : isMostCommonAction ? (
                                                        // Display Most Common Action with medium font
                                                        <p className="text-base font-semibold text-gray-900">{String(value)}</p>
                                                    ) : isNumeric ? (
                                                        // Display numeric values with large font
                                                        <p className="text-2xl font-bold text-gray-900">{String(value)}</p>
                                                    ) : (
                                                        // Display other values with medium font
                                                        <p className="text-base font-semibold text-gray-900">{String(value)}</p>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Visualizations */}
                            {renderChart() && (
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Visualizations</h3>
                                    {renderChart()}
                                </div>
                            )}

                            {/* Data Table */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-3">Report Data</h3>
                                <div className="border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="overflow-x-auto max-h-96">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50 sticky top-0">
                                                <tr>
                                                    {report.dataSnapshot.columns.map((column, index) => (
                                                        <th
                                                            key={index}
                                                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                                                        >
                                                            {column.label}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {report.dataSnapshot.rows.length === 0 ? (
                                                    <tr>
                                                        <td
                                                            colSpan={report.dataSnapshot.columns.length}
                                                            className="px-4 py-8 text-center text-gray-500"
                                                        >
                                                            No data available
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    report.dataSnapshot.rows.map((row, rowIndex) => (
                                                        <tr key={rowIndex} className="hover:bg-gray-50">
                                                            {report.dataSnapshot.columns.map((column, cellIndex) => {
                                                                const cellValue = row[column.key];
                                                                return (
                                                                    <td
                                                                        key={cellIndex}
                                                                        className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap"
                                                                    >
                                                                        {cellValue !== null && cellValue !== undefined ? String(cellValue) : '-'}
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>

                {/* Modal Footer */}
                <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between bg-gray-50">
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={handlePinReport}
                            disabled={!report}
                            className={`px-4 py-2 rounded-lg border transition-colors flex items-center ${
                                report?.isPinned
                                    ? 'border-yellow-500 bg-yellow-50 text-yellow-700 hover:bg-yellow-100'
                                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            <Pin className="w-4 h-4 mr-2" fill={report?.isPinned ? 'currentColor' : 'none'} />
                            {report?.isPinned ? 'Unpin' : 'Pin Report'}
                        </button>
                        <button
                            onClick={handleRegenerate}
                            disabled={!report || isRegenerating}
                            className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors flex items-center"
                        >
                            <RefreshCw className={`w-4 h-4 mr-2 ${isRegenerating ? 'animate-spin' : ''}`} />
                            {isRegenerating ? 'Regenerating...' : 'Regenerate'}
                        </button>
                    </div>
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={handleExportPDF}
                            disabled={!report}
                            className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors flex items-center"
                        >
                            <FileText className="w-4 h-4 mr-2" />
                            Export PDF
                        </button>
                        <button
                            onClick={handleExportCSV}
                            disabled={!report}
                            className="px-4 py-2 rounded-lg text-white transition-colors flex items-center"
                            style={{ backgroundColor: report ? '#69BE28' : '#d1d5db' }}
                            onMouseEnter={(e) => report && (e.currentTarget.style.backgroundColor = '#5ba023')}
                            onMouseLeave={(e) => report && (e.currentTarget.style.backgroundColor = '#69BE28')}
                        >
                            <FileSpreadsheet className="w-4 h-4 mr-2" />
                            Export CSV
                        </button>
                        <button
                            onClick={onClose}
                            className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Report History Tab Component
const ReportHistoryTab: React.FC<{ user: any; initialReportId?: string | null }> = ({ user, initialReportId }) => {
    const [reports, setReports] = useState<Report[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Filters
    const [reportTypeFilter, setReportTypeFilter] = useState<ReportType | 'all'>('all');
    const [searchTerm, setSearchTerm] = useState('');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const pageSize = 10;

    // View modal state
    const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
    const [hasOpenedInitialReport, setHasOpenedInitialReport] = useState(false);

    // Share modal state
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [reportToShare, setReportToShare] = useState<{ id: string; name: string; ownerId: string; isOrgWide: boolean } | null>(null);

    // Download menu state
    const [openDownloadMenu, setOpenDownloadMenu] = useState<string | null>(null);

    // Delete report confirmation modal
    const [deleteReportConfirm, setDeleteReportConfirm] = useState<{ id: string; name: string } | null>(null);
    const [isDeletingReport, setIsDeletingReport] = useState(false);

    // Automatically open report if initialReportId is provided (only once)
    useEffect(() => {
        if (initialReportId && !hasOpenedInitialReport) {
            setSelectedReportId(initialReportId);
            setHasOpenedInitialReport(true);
        }
    }, [initialReportId, hasOpenedInitialReport]);

    // Load reports
    const loadReports = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await apiClient.getReportHistory({
                reportType: reportTypeFilter,
                page: currentPage,
                pageSize
            });

            setReports(response.data);
            setTotalCount(response.totalCount);
            setTotalPages(response.totalPages);
        } catch (err: any) {
            console.error('Error loading reports:', err);
            setError(err.message || 'Failed to load reports');
            toast.error('Failed to load reports');
        } finally {
            setIsLoading(false);
        }
    }, [reportTypeFilter, currentPage]);

    // Load reports on mount and when filters change
    useEffect(() => {
        loadReports();
    }, [loadReports]);

    // Close download menu when clicking outside
    useEffect(() => {
        const handleClickOutside = () => setOpenDownloadMenu(null);
        if (openDownloadMenu) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [openDownloadMenu]);

    // Filter reports by search term (client-side)
    const filteredReports = reports.filter(report =>
        report.reportName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Handle pin/unpin
    const handlePinReport = async (reportId: string, currentlyPinned: boolean) => {
        try {
            await apiClient.pinReport(reportId, !currentlyPinned);
            toast.success(currentlyPinned ? 'Report unpinned' : 'Report pinned');
            loadReports();
        } catch (err: any) {
            toast.error(err.message || 'Failed to update report');
        }
    };

    // Handle delete
    const handleDeleteReport = (reportId: string, reportName: string) => {
        setDeleteReportConfirm({ id: reportId, name: reportName });
    };

    const confirmDeleteReport = async () => {
        if (!deleteReportConfirm) return;
        try {
            setIsDeletingReport(true);
            await apiClient.deleteReport(deleteReportConfirm.id);
            toast.success(`"${deleteReportConfirm.name}" deleted`);
            setDeleteReportConfirm(null);
            loadReports();
        } catch (err: any) {
            toast.error(err.message || 'Failed to delete report');
        } finally {
            setIsDeletingReport(false);
        }
    };

    // Get badge color for report type
    const getReportTypeBadgeColor = (type: ReportType) => {
        switch (type) {
            case 'compliance':
                return 'bg-blue-100 text-blue-800';
            case 'risk_analysis':
                return 'bg-red-100 text-red-800';
            case 'licence_check':
                return 'bg-green-100 text-green-800';
            case 'email_delivery':
                return 'bg-purple-100 text-purple-800';
            case 'audit_activity':
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    // Get formatted report type label
    const getReportTypeLabel = (type: ReportType) => {
        switch (type) {
            case 'compliance':
                return 'Compliance';
            case 'risk_analysis':
                return 'Risk Analysis';
            case 'licence_check':
                return 'Licence Check';
            case 'email_delivery':
                return 'Email Delivery';
            case 'audit_activity':
                return 'Audit Activity';
            default:
                return type;
        }
    };

    // Calculate summary stats
    const totalReports = totalCount;
    const pinnedReports = reports.filter(r => r.isPinned).length;
    const thisMonthReports = reports.filter(r => {
        const reportDate = new Date(r.generatedAt);
        const now = new Date();
        return reportDate.getMonth() === now.getMonth() && reportDate.getFullYear() === now.getFullYear();
    }).length;

    return (
        <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Total Reports</p>
                            <p className="text-2xl font-bold text-gray-900">{totalReports}</p>
                        </div>
                        <FileText className="w-8 h-8 text-gray-400" />
                    </div>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">This Month</p>
                            <p className="text-2xl font-bold text-gray-900">{thisMonthReports}</p>
                        </div>
                        <Calendar className="w-8 h-8 text-gray-400" />
                    </div>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Pinned</p>
                            <p className="text-2xl font-bold text-gray-900">{pinnedReports}</p>
                        </div>
                        <Pin className="w-8 h-8 text-gray-400" />
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Search */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Search Reports
                        </label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search by report name..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    {/* Report Type Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Report Type
                        </label>
                        <select
                            value={reportTypeFilter}
                            onChange={(e) => {
                                setReportTypeFilter(e.target.value as any);
                                setCurrentPage(1);
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        >
                            <option value="all">All Types</option>
                            <option value="compliance">Compliance</option>
                            <option value="risk_analysis">Risk Analysis</option>
                            <option value="licence_check">Licence Check</option>
                            <option value="email_delivery">Email Delivery</option>
                            <option value="audit_activity">Audit Activity</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Reports Table */}
            {isLoading ? (
                <div className="bg-white rounded-lg border border-gray-200 p-12">
                    <div className="flex flex-col items-center justify-center">
                        <Loader2 className="w-12 h-12 text-gray-400 animate-spin mb-4" />
                        <p className="text-gray-600">Loading reports...</p>
                    </div>
                </div>
            ) : error ? (
                <div className="bg-white rounded-lg border border-red-200 p-12">
                    <div className="flex flex-col items-center justify-center">
                        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
                        <p className="text-gray-900 font-medium mb-2">Error Loading Reports</p>
                        <p className="text-gray-600 mb-4">{error}</p>
                        <button
                            onClick={loadReports}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                            style={{ backgroundColor: '#69BE28' }}
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            ) : filteredReports.length === 0 ? (
                <div className="bg-white rounded-lg border border-gray-200 p-12">
                    <div className="flex flex-col items-center justify-center">
                        <FileText className="w-12 h-12 text-gray-400 mb-4" />
                        <p className="text-gray-900 font-medium mb-2">No Reports Found</p>
                        <p className="text-gray-600">
                            {searchTerm
                                ? 'Try adjusting your search or filters'
                                : 'Generate your first report to get started'}
                        </p>
                    </div>
                </div>
            ) : (
                <>
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Report Name
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Type
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Generated
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Generated By
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Rows
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredReports.map((report) => (
                                        <tr key={report.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    {report.isPinned && (
                                                        <Pin className="w-4 h-4 text-yellow-500 mr-2" fill="currentColor" />
                                                    )}
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {report.reportName}
                                                        </div>
                                                        {report.daysUntilExpiration !== null && !report.isPinned && (
                                                            <div className="text-xs text-gray-500">
                                                                {report.daysUntilExpiration > 0
                                                                    ? `Expires in ${report.daysUntilExpiration} days`
                                                                    : 'Expired'}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getReportTypeBadgeColor(report.type)}`}>
                                                    {getReportTypeLabel(report.type)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {new Date(report.generatedAt).toLocaleDateString('en-GB')}
                                                <div className="text-xs text-gray-500">
                                                    {new Date(report.generatedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {report.generatedBy.name}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {report.rowCount}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {report.status === 'completed' ? (
                                                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                                        Completed
                                                    </span>
                                                ) : report.status === 'failed' ? (
                                                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                                                        Failed
                                                    </span>
                                                ) : (
                                                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                                                        Generating
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex items-center justify-end space-x-2">
                                                    <button
                                                        onClick={() => setSelectedReportId(report.id)}
                                                        className="text-gray-600 hover:text-gray-900"
                                                        title="View Report"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>

                                                    {/* Download Menu */}
                                                    <div className="relative">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setOpenDownloadMenu(openDownloadMenu === report.id ? null : report.id);
                                                            }}
                                                            className="text-gray-600 hover:text-gray-900 flex items-center"
                                                            title="Download Report"
                                                        >
                                                            <Download className="w-4 h-4" />
                                                            <ChevronDown className="w-3 h-3 ml-0.5" />
                                                        </button>

                                                        {/* Dropdown Menu */}
                                                        {openDownloadMenu === report.id && (
                                                            <div className="absolute right-0 mt-2 w-32 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                                                                <button
                                                                    onClick={async () => {
                                                                        try {
                                                                            await apiClient.exportReportCSV(report.id, report.reportName);
                                                                            toast.success('CSV downloaded successfully');
                                                                            setOpenDownloadMenu(null);
                                                                        } catch (error) {
                                                                            toast.error('Failed to download CSV');
                                                                        }
                                                                    }}
                                                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-t-lg flex items-center"
                                                                >
                                                                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                                                                    CSV
                                                                </button>
                                                                <button
                                                                    onClick={async () => {
                                                                        try {
                                                                            await apiClient.exportReportPDF(report.id, report.reportName);
                                                                            toast.success('PDF downloaded successfully');
                                                                            setOpenDownloadMenu(null);
                                                                        } catch (error) {
                                                                            toast.error('Failed to download PDF');
                                                                        }
                                                                    }}
                                                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-b-lg flex items-center"
                                                                >
                                                                    <FileText className="w-4 h-4 mr-2" />
                                                                    PDF
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {(user.role === 'admin' || report.generatedBy.id === user.id) && (
                                                        <button
                                                            onClick={() => {
                                                                setReportToShare({
                                                                    id: report.id,
                                                                    name: report.reportName,
                                                                    ownerId: report.generatedBy.id,
                                                                    isOrgWide: report.isOrgWide
                                                                });
                                                                setShareModalOpen(true);
                                                            }}
                                                            className="text-gray-600 hover:text-blue-600"
                                                            title="Share Report"
                                                        >
                                                            <Share2 className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handlePinReport(report.id, report.isPinned)}
                                                        className={`hover:text-yellow-600 ${report.isPinned ? 'text-yellow-500' : 'text-gray-600'}`}
                                                        title={report.isPinned ? 'Unpin Report' : 'Pin Report'}
                                                    >
                                                        <Pin className="w-4 h-4" fill={report.isPinned ? 'currentColor' : 'none'} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteReport(report.id, report.reportName)}
                                                        className="text-gray-600 hover:text-red-600"
                                                        title="Delete Report"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 flex items-center justify-between">
                            <div className="flex-1 flex justify-between sm:hidden">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1}
                                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    disabled={currentPage === totalPages}
                                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-sm text-gray-700">
                                        Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                                        <span className="font-medium">{Math.min(currentPage * pageSize, totalCount)}</span> of{' '}
                                        <span className="font-medium">{totalCount}</span> reports
                                    </p>
                                </div>
                                <div>
                                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            disabled={currentPage === 1}
                                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        >
                                            Previous
                                        </button>
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                            <button
                                                key={page}
                                                onClick={() => setCurrentPage(page)}
                                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                                    currentPage === page
                                                        ? 'z-10 bg-green-50 border-green-500 text-green-600'
                                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                }`}
                                                style={currentPage === page ? { borderColor: '#69BE28', color: '#69BE28', backgroundColor: '#f0fdf4' } : {}}
                                            >
                                                {page}
                                            </button>
                                        ))}
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                            disabled={currentPage === totalPages}
                                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        >
                                            Next
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Report View Modal */}
            {selectedReportId && (
                <ReportViewModal
                    reportId={selectedReportId}
                    onClose={() => setSelectedReportId(null)}
                    onRefresh={loadReports}
                />
            )}

            {/* Share Report Modal */}
            {shareModalOpen && reportToShare && (
                <ShareReportModal
                    isOpen={shareModalOpen}
                    onClose={() => {
                        setShareModalOpen(false);
                        setReportToShare(null);
                    }}
                    reportId={reportToShare.id}
                    reportName={reportToShare.name}
                    reportOwnerId={reportToShare.ownerId}
                    reportIsOrgWide={reportToShare.isOrgWide}
                    currentUserId={user.id}
                    onSuccess={() => {
                        loadReports();
                        toast.success('Report shared successfully');
                    }}
                />
            )}

            {/* Delete Report Confirmation Modal */}
            {deleteReportConfirm && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => !isDeletingReport && setDeleteReportConfirm(null)} />
                        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                            <div className="flex items-center space-x-3 mb-4">
                                <div className="p-2 bg-red-100 rounded-full">
                                    <Trash2 className="w-5 h-5 text-red-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Delete Report</h3>
                            </div>
                            <p className="text-sm text-gray-600 mb-1">
                                Are you sure you want to delete <span className="font-medium text-gray-900">"{deleteReportConfirm.name}"</span>?
                            </p>
                            <p className="text-sm text-gray-500 mb-6">This action cannot be undone.</p>
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => setDeleteReportConfirm(null)}
                                    disabled={isDeletingReport}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDeleteReport}
                                    disabled={isDeletingReport}
                                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2"
                                >
                                    {isDeletingReport && <Loader2 className="w-4 h-4 animate-spin" />}
                                    <span>{isDeletingReport ? 'Deleting...' : 'Delete'}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Scheduled Reports Tab Component
const ScheduledReportsTab: React.FC<{ user: any }> = ({ user }) => {
    const [schedules, setSchedules] = useState<ReportSchedule[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [filterType, setFilterType] = useState<ReportType | 'all'>('all');
    const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');
    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
    const [selectedSchedule, setSelectedSchedule] = useState<ReportSchedule | null>(null);
    const [deleteScheduleConfirm, setDeleteScheduleConfirm] = useState<{ id: string; name: string } | null>(null);
    const [isDeletingSchedule, setIsDeletingSchedule] = useState(false);

    useEffect(() => {
        loadSchedules();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterType, filterActive]);

    const loadSchedules = async () => {
        try {
            setIsLoading(true);
            const params: any = {};
            if (filterType !== 'all') params.reportType = filterType;
            if (filterActive === 'active') params.isActive = true;
            if (filterActive === 'inactive') params.isActive = false;

            const data = await apiClient.getReportSchedules(params);
            setSchedules(data);
        } catch (error: any) {
            toast.error(error.message || 'Failed to load schedules');
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggleActive = async (schedule: ReportSchedule) => {
        try {
            await apiClient.updateReportSchedule(schedule.id, { isActive: !schedule.isActive });
            toast.success(`Schedule ${schedule.isActive ? 'paused' : 'activated'}`);
            loadSchedules();
        } catch (error: any) {
            toast.error(error.message || 'Failed to update schedule');
        }
    };

    const handleRunNow = async (schedule: ReportSchedule) => {
        try {
            await apiClient.runScheduleNow(schedule.id);
            toast.success('Report generation started');
        } catch (error: any) {
            toast.error(error.message || 'Failed to run schedule');
        }
    };

    const handleDeleteSchedule = (scheduleId: string, scheduleName: string) => {
        setDeleteScheduleConfirm({ id: scheduleId, name: scheduleName });
    };

    const confirmDeleteSchedule = async () => {
        if (!deleteScheduleConfirm) return;
        try {
            setIsDeletingSchedule(true);
            await apiClient.deleteReportSchedule(deleteScheduleConfirm.id);
            toast.success(`"${deleteScheduleConfirm.name}" schedule deleted`);
            setDeleteScheduleConfirm(null);
            loadSchedules();
        } catch (error: any) {
            toast.error(error.message || 'Failed to delete schedule');
        } finally {
            setIsDeletingSchedule(false);
        }
    };

    const formatNextRun = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = date.getTime() - now.getTime();
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffHours / 24);

        // Format the actual date and time
        const dateFormat = date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
        const timeFormat = date.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit'
        });

        let relativeTime = '';
        if (diffMs < 0) {
            relativeTime = 'Overdue';
        } else if (diffHours < 1) {
            relativeTime = 'In less than 1 hour';
        } else if (diffHours < 24) {
            relativeTime = `In ${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
        } else {
            relativeTime = `In ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
        }

        return `${relativeTime} (${dateFormat} at ${timeFormat})`;
    };

    const getFrequencyLabel = (frequency: string) => {
        const labels: Record<string, string> = {
            'daily': 'Daily',
            'weekly': 'Weekly',
            'monthly': 'Monthly',
            'quarterly': 'Quarterly'
        };
        return labels[frequency] || frequency;
    };

    const getReportTypeLabel = (type: ReportType) => {
        const labels: Record<ReportType, string> = {
            'compliance': 'Compliance',
            'risk_analysis': 'Risk Analysis',
            'licence_check': 'Licence Check',
            'email_delivery': 'Email Delivery',
            'audit_activity': 'Audit Activity'
        };
        return labels[type] || type;
    };

    return (
        <div className="space-y-6">
            {/* Scheduled Report Visibility Info Box */}
            <div className="flex items-start space-x-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Who can see scheduled reports?</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                        <li><strong>Scheduled reports</strong> are automatically generated and saved to Report History</li>
                        <li><strong>You</strong> will receive a notification when a scheduled report is generated</li>
                        <li><strong>Visibility rules</strong> are the same as manually generated reports (see Generate Report tab)</li>
                        <li><strong>Organisation-wide schedules</strong> (created by admins) generate reports visible to everyone</li>
                        <li><strong>Personal schedules</strong> generate reports visible only to you, admins, managers, and compliance officers</li>
                    </ul>
                </div>
            </div>

            {/* Header with Create Button */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">Manage Scheduled Reports</h2>
                    <p className="text-sm text-gray-600 mt-1">
                        Automate report generation with custom schedules
                    </p>
                </div>
                <button
                    onClick={() => {
                        setSelectedSchedule(null);
                        setIsScheduleModalOpen(true);
                    }}
                    className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center space-x-2"
                    style={{ backgroundColor: '#69BE28' }}
                >
                    <Calendar className="w-4 h-4" />
                    <span>Create Schedule</span>
                </button>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Report Type</label>
                    <select
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value as any)}
                        className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-opacity-50 p-2"
                        
                    >
                        <option value="all">All Types</option>
                        <option value="compliance">Compliance</option>
                        <option value="risk_analysis">Risk Analysis</option>
                        <option value="licence_check">Licence Check</option>
                        <option value="email_delivery">Email Delivery</option>
                        <option value="audit_activity">Audit Activity</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                        value={filterActive}
                        onChange={(e) => setFilterActive(e.target.value as any)}
                        className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-opacity-50 p-2"
                        
                    >
                        <option value="all">All Schedules</option>
                        <option value="active">Active Only</option>
                        <option value="inactive">Paused Only</option>
                    </select>
                </div>
            </div>

            {/* Schedules List */}
            {isLoading ? (
                <div className="flex justify-center items-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#69BE28' }} />
                </div>
            ) : schedules.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <Clock className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                    <p className="text-gray-600 font-medium mb-2">No Scheduled Reports</p>
                    <p className="text-sm text-gray-500 mb-4">
                        Create a schedule to automatically generate reports
                    </p>
                    <button
                        onClick={() => {
                            setSelectedSchedule(null);
                            setIsScheduleModalOpen(true);
                        }}
                        className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
                        style={{ backgroundColor: '#69BE28' }}
                    >
                        Create Your First Schedule
                    </button>
                </div>
            ) : (
                <div className="grid gap-4">
                    {schedules.map((schedule) => (
                        <div
                            key={schedule.id}
                            className={`bg-white border rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow ${
                                !schedule.isActive ? 'opacity-60' : ''
                            }`}
                        >
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                                {/* Schedule Info */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center space-x-3 mb-2">
                                        <h3 className="text-lg font-semibold text-gray-900 truncate">
                                            {schedule.reportName}
                                        </h3>
                                        <span
                                            className="px-2 py-1 text-xs font-medium rounded-full"
                                            style={{
                                                backgroundColor: schedule.isActive ? '#dcfce7' : '#fee2e2',
                                                color: schedule.isActive ? '#166534' : '#991b1b'
                                            }}
                                        >
                                            {schedule.isActive ? 'Active' : 'Paused'}
                                        </span>
                                        {schedule.isOrgWide && (
                                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                                Organisation-wide
                                            </span>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600">
                                        <div className="flex items-center space-x-2">
                                            <BarChart3 className="w-4 h-4" />
                                            <span>{getReportTypeLabel(schedule.type)}</span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <Clock className="w-4 h-4" />
                                            <span>{getFrequencyLabel(schedule.frequency)}</span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <Calendar className="w-4 h-4" />
                                            <span>Next: {formatNextRun(schedule.nextRunAt)}</span>
                                        </div>
                                        {schedule.lastRunAt && (
                                            <div className="flex items-center space-x-2">
                                                <CheckCircle className="w-4 h-4" />
                                                <span>
                                                    Last run: {new Date(schedule.lastRunAt).toLocaleDateString('en-GB')}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex items-center space-x-2 lg:ml-4">
                                    <button
                                        onClick={() => handleToggleActive(schedule)}
                                        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                                        title={schedule.isActive ? 'Pause' : 'Activate'}
                                    >
                                        {schedule.isActive ? (
                                            <AlertCircle className="w-5 h-5" />
                                        ) : (
                                            <CheckCircle className="w-5 h-5" />
                                        )}
                                    </button>
                                    <button
                                        onClick={() => handleRunNow(schedule)}
                                        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                                        title="Run now"
                                    >
                                        <RefreshCw className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={() => {
                                            setSelectedSchedule(schedule);
                                            setIsScheduleModalOpen(true);
                                        }}
                                        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                                        title="Edit"
                                    >
                                        <FileText className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={() => handleDeleteSchedule(schedule.id, schedule.reportName)}
                                        className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                                        title="Delete"
                                    >
                                        <Trash2 className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Delete Schedule Confirmation Modal */}
            {deleteScheduleConfirm && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => !isDeletingSchedule && setDeleteScheduleConfirm(null)} />
                        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                            <div className="flex items-center space-x-3 mb-4">
                                <div className="p-2 bg-red-100 rounded-full">
                                    <Trash2 className="w-5 h-5 text-red-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Delete Schedule</h3>
                            </div>
                            <p className="text-sm text-gray-600 mb-1">
                                Are you sure you want to delete the schedule for <span className="font-medium text-gray-900">"{deleteScheduleConfirm.name}"</span>?
                            </p>
                            <p className="text-sm text-gray-500 mb-6">No further reports will be generated automatically for this schedule.</p>
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => setDeleteScheduleConfirm(null)}
                                    disabled={isDeletingSchedule}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDeleteSchedule}
                                    disabled={isDeletingSchedule}
                                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2"
                                >
                                    {isDeletingSchedule && <Loader2 className="w-4 h-4 animate-spin" />}
                                    <span>{isDeletingSchedule ? 'Deleting...' : 'Delete'}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Schedule Modal */}
            <ScheduleModal
                isOpen={isScheduleModalOpen}
                onClose={() => {
                    setIsScheduleModalOpen(false);
                    setSelectedSchedule(null);
                }}
                schedule={selectedSchedule}
                user={user}
                onSuccess={loadSchedules}
            />
        </div>
    );
};

// Shared with Me Tab Component
const SharedWithMeTab: React.FC<{ user: any }> = ({ user }) => {
    const [reports, setReports] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Filters
    const [reportTypeFilter, setReportTypeFilter] = useState<ReportType | 'all'>('all');
    const [searchTerm, setSearchTerm] = useState('');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const pageSize = 10;

    // View modal state
    const [selectedReportId, setSelectedReportId] = useState<string | null>(null);

    // Download menu state
    const [openDownloadMenu, setOpenDownloadMenu] = useState<string | null>(null);

    // Load shared reports
    const loadSharedReports = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await apiClient.getSharedWithMeReports({
                reportType: reportTypeFilter,
                page: currentPage,
                pageSize
            });

            setReports(response.data);
            setTotalCount(response.totalCount);
            setTotalPages(response.totalPages);
        } catch (err: any) {
            console.error('Error loading shared reports:', err);
            setError(err.message || 'Failed to load shared reports');
            toast.error('Failed to load shared reports');
        } finally {
            setIsLoading(false);
        }
    }, [reportTypeFilter, currentPage]);

    // Load reports on mount and when filters change
    useEffect(() => {
        loadSharedReports();
    }, [loadSharedReports]);

    // Close download menu when clicking outside
    useEffect(() => {
        const handleClickOutside = () => setOpenDownloadMenu(null);
        if (openDownloadMenu) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [openDownloadMenu]);

    // Filter reports by search term (client-side)
    const filteredReports = reports.filter(report =>
        report.reportName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatReportType = (type: string) => {
        const typeMap: Record<string, string> = {
            compliance: 'Compliance',
            risk_analysis: 'Risk Analysis',
            licence_check: 'Licence Check',
            email_delivery: 'Email Delivery',
            audit_activity: 'Audit Activity'
        };
        return typeMap[type] || type;
    };

    const getTypeColor = (type: string) => {
        const colorMap: Record<string, string> = {
            compliance: 'bg-blue-100 text-blue-800',
            risk_analysis: 'bg-red-100 text-red-800',
            licence_check: 'bg-green-100 text-green-800',
            email_delivery: 'bg-purple-100 text-purple-800',
            audit_activity: 'bg-yellow-100 text-yellow-800'
        };
        return colorMap[type] || 'bg-gray-100 text-gray-800';
    };

    return (
        <div className="space-y-6">
            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Report Type Filter */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Report Type
                    </label>
                    <select
                        value={reportTypeFilter}
                        onChange={(e) => {
                            setReportTypeFilter(e.target.value as ReportType | 'all');
                            setCurrentPage(1);
                        }}
                        className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-opacity-50 p-3"
                    >
                        <option value="all">All Types</option>
                        <option value="compliance">Compliance</option>
                        <option value="risk_analysis">Risk Analysis</option>
                        <option value="licence_check">Licence Check</option>
                        <option value="email_delivery">Email Delivery</option>
                        <option value="audit_activity">Audit Activity</option>
                    </select>
                </div>

                {/* Search */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Search Reports
                    </label>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Search by report name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-opacity-50"
                        />
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500">Total Shared Reports</p>
                            <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
                        </div>
                        <Users className="w-10 h-10" style={{ color: '#69BE28' }} />
                    </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500">Displayed Reports</p>
                            <p className="text-2xl font-bold text-gray-900">{filteredReports.length}</p>
                        </div>
                        <FileText className="w-10 h-10" style={{ color: '#69BE28' }} />
                    </div>
                </div>
            </div>

            {/* Reports Table */}
            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#69BE28' }} />
                    <span className="ml-3 text-gray-600">Loading shared reports...</span>
                </div>
            ) : error ? (
                <div className="flex items-center justify-center py-12 text-red-600">
                    <AlertCircle className="w-6 h-6 mr-2" />
                    <span>{error}</span>
                </div>
            ) : filteredReports.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                    <Users className="w-16 h-16 mb-4 text-gray-300" />
                    <p className="text-lg font-medium">No shared reports found</p>
                    <p className="text-sm mt-2">
                        {searchTerm ? 'Try adjusting your search' : 'Reports shared with you will appear here'}
                    </p>
                </div>
            ) : (
                <div className="overflow-x-auto bg-white rounded-lg shadow">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Report Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Type
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Shared By
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Shared Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Row Count
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredReports.map((report) => (
                                <tr key={report.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center">
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">
                                                    {report.reportName}
                                                </div>
                                                {report.shareNote && (
                                                    <div className="text-xs text-gray-500 italic mt-1">
                                                        Note: {report.shareNote}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(report.type)}`}>
                                            {formatReportType(report.type)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {report.generatedBy.firstName} {report.generatedBy.lastName}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(report.sharedAt).toLocaleDateString('en-GB', {
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric'
                                        })}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {report.rowCount.toLocaleString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex items-center space-x-2">
                                            {/* View Button */}
                                            <button
                                                onClick={() => setSelectedReportId(report.id)}
                                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                title="View Report"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </button>

                                            {/* Download Menu */}
                                            <div className="relative">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setOpenDownloadMenu(openDownloadMenu === report.id ? null : report.id);
                                                    }}
                                                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors flex items-center"
                                                    title="Download Report"
                                                >
                                                    <Download className="w-4 h-4" />
                                                    <ChevronDown className="w-3 h-3 ml-1" />
                                                </button>

                                                {/* Dropdown Menu */}
                                                {openDownloadMenu === report.id && (
                                                    <div className="absolute right-0 mt-2 w-32 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                                                        <button
                                                            onClick={async () => {
                                                                try {
                                                                    await apiClient.exportReportCSV(report.id, report.reportName);
                                                                    toast.success('CSV downloaded successfully');
                                                                    setOpenDownloadMenu(null);
                                                                } catch (error) {
                                                                    toast.error('Failed to download CSV');
                                                                }
                                                            }}
                                                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-t-lg flex items-center"
                                                        >
                                                            <FileSpreadsheet className="w-4 h-4 mr-2" />
                                                            CSV
                                                        </button>
                                                        <button
                                                            onClick={async () => {
                                                                try {
                                                                    await apiClient.exportReportPDF(report.id, report.reportName);
                                                                    toast.success('PDF downloaded successfully');
                                                                    setOpenDownloadMenu(null);
                                                                } catch (error) {
                                                                    toast.error('Failed to download PDF');
                                                                }
                                                            }}
                                                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-b-lg flex items-center"
                                                        >
                                                            <FileText className="w-4 h-4 mr-2" />
                                                            PDF
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                        Showing page {currentPage} of {totalPages} ({totalCount} total reports)
                    </div>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            {/* Report View Modal */}
            {selectedReportId && (
                <ReportViewModal
                    reportId={selectedReportId}
                    onClose={() => setSelectedReportId(null)}
                    onRefresh={loadSharedReports}
                />
            )}
        </div>
    );
};

export default Reports;
